'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { countryToIso } from '@sola/schema/dotcom/shared/geo.ts';
import { trackViewContentList } from '@sola/tracking/components/datalayer.tsx';
import type { ContentFeedCardProps } from '../content-feed-card/content-feed-card.tsx';
import { ContentFeedCard } from '../content-feed-card/content-feed-card.tsx';
import { useContentFeed } from './content-feed.logic.ts';
import type { ContentSource } from './content-feed.suppression.ts';
import type { ContentFeedEntryResolved, ContentFeedListResponse } from './content-feed.types.ts';

/**
 * Resolves a country display name from an ISO code using i18n geo translations.
 * Handles Compassion's non-standard codes (DR, ES, GU, etc.) via countryToIso.
 */
const resolveCountryName = (isoCode: string | undefined, t: ReturnType<typeof useTranslation>['t']): string | undefined => {
  if (!isoCode) {
    return undefined;
  }
  const normalized = countryToIso(isoCode);
  const translated = t(`geo:countries.${normalized}.nameS`);
  // If the key isn't found, i18next returns the key itself
  return translated.startsWith('geo:') ? undefined : translated;
};
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Maps an API entry to ContentFeedCard props.
 */
const mapEntryToCardProps = (entry: ContentFeedEntryResolved, t: ReturnType<typeof useTranslation>['t']): ContentFeedCardProps => {
  const isNew = !!entry.publishedAt && Date.now() - new Date(entry.publishedAt).getTime() < SEVEN_DAYS_MS;
  const props: ContentFeedCardProps = {
    sourceType: entry.source,
    title: entry.title || '',
    description: entry.description || undefined,
    media: entry.media,
    publishedAt: entry.publishedAt,
    author: entry.author,
    match: entry.match,
    programCountryIsoCode: entry.programCountry?.isoCode,
    programCountryName: resolveCountryName(entry.programCountry?.isoCode, t),
    hasNewUpdate: isNew,
    icon: entry.fcp ? 'fcp' : undefined
  };
  if (entry.source === 'causes' && entry.metadata) {
    if (entry.metadata.fundingProgress) {
      props.fundingProgress = entry.metadata.fundingProgress;
    }
    if (entry.metadata.link) {
      props.link = entry.metadata.link;
    }
  }
  return props;
};
export interface ContentFeedProps {
  /** Override the default API fetch function (used for Storybook/testing) */
  fetchOverride?: (params: {
    cursor: string | null;
  }) => Promise<ContentFeedListResponse>;
  /** Heading title for the feed section */
  title?: string;
  /** Subtitle/description below the heading */
  subtitle?: string;
  /**
   * Which content sources are enabled for this block instance.
   * Configured via Contentful advanced block settings.
   * Defaults to all sources enabled.
   */
  enabledSources?: Record<ContentSource, boolean>;
}

/**
 * Content Feed advanced block.
 * Displays a paginated feed of content entries (community, causes, compassion.media)
 * from the Supporter Global Content API.
 * Applies suppression rules to hide the feed when content is insufficient or stale.
 */
export const ContentFeed: React.FC<ContentFeedProps> = t0 => {
  const $ = _c(37);
  const {
    fetchOverride,
    title,
    subtitle,
    enabledSources
  } = t0;
  const {
    entries,
    isLoading,
    hasMore,
    loadMore,
    t,
    shouldRender,
    participantNameMap
  } = useContentFeed(enabledSources, fetchOverride);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["geo"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t: tGeo
  } = useTranslation(t1);
  const prevEntryCountRef = useRef(0);
  let t2;
  let t3;
  if ($[1] !== entries) {
    t2 = () => {
      if (entries.length === 0 || entries.length === prevEntryCountRef.current) {
        return;
      }
      const newEntries = entries.slice(prevEntryCountRef.current);
      prevEntryCountRef.current = entries.length;
      trackViewContentList("content-feed", "My Compassion Content Feed", newEntries.map(_temp));
    };
    t3 = [entries];
    $[1] = entries;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== t || $[5] !== title) {
    t4 = title || t("my:contentFeed.heading");
    $[4] = t;
    $[5] = title;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const headingTitle = t4;
  let t5;
  if ($[7] !== subtitle || $[8] !== t) {
    t5 = subtitle || t("my:contentFeed.subtitle");
    $[7] = subtitle;
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const headingSubtitle = t5;
  if (isLoading && entries.length === 0) {
    let t6;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="flex flex-col gap-snug-sm pt-comfy-xs pb-snug-xl"><Skeleton height={30} width={200} borderRadius="var(--outline-radius-sm)" /><Skeleton height={48} width={500} borderRadius="var(--outline-radius-sm)" /></div>;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    let t7;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="ContentFeed max-w-md flex flex-col gap-comfy-xl" aria-busy="true">{t6}<div className="flex flex-col gap-comfy-xl"><Skeleton height={300} borderRadius="var(--outline-radius-lg)" /><Skeleton height={300} borderRadius="var(--outline-radius-lg)" /></div></div>;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    return t7;
  }
  if (!shouldRender) {
    return null;
  }
  let t6;
  if ($[12] !== headingTitle) {
    t6 = <h2 className="heading-sm heading-neutral-amplified">{headingTitle}</h2>;
    $[12] = headingTitle;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== headingSubtitle) {
    t7 = <p className="body-sm label-neutral-mild">{headingSubtitle}</p>;
    $[14] = headingSubtitle;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t6 || $[17] !== t7) {
    t8 = <div className="pt-comfy-xs pb-snug-xl">{t6}{t7}</div>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== entries || $[20] !== participantNameMap || $[21] !== tGeo) {
    let t10;
    if ($[23] !== participantNameMap || $[24] !== tGeo) {
      t10 = entry_0 => <ContentFeedCard key={entry_0.id} entryId={entry_0.id} participantNameMap={participantNameMap} {...mapEntryToCardProps(entry_0, tGeo)} />;
      $[23] = participantNameMap;
      $[24] = tGeo;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    t9 = entries.map(t10);
    $[19] = entries;
    $[20] = participantNameMap;
    $[21] = tGeo;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[26] !== t9) {
    t10 = <div className="flex flex-col gap-comfy-lg">{t9}</div>;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== hasMore || $[29] !== isLoading || $[30] !== loadMore || $[31] !== t) {
    t11 = hasMore && <div className="flex justify-center mt-comfy-xl" aria-live="polite"><ButtonStyle variant="secondary" size="lg"><button type="button" onClick={loadMore} disabled={isLoading}>{isLoading ? t("my:contentFeed.loading") : t("my:contentFeed.loadMore")}</button></ButtonStyle></div>;
    $[28] = hasMore;
    $[29] = isLoading;
    $[30] = loadMore;
    $[31] = t;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t10 || $[34] !== t11 || $[35] !== t8) {
    t12 = <div className="ContentFeed max-w-md flex flex-col gap-comfy-xl">{t8}{t10}{t11}</div>;
    $[33] = t10;
    $[34] = t11;
    $[35] = t8;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  return t12;
};
function _temp(entry) {
  return {
    content_id: entry.id,
    content_type: entry.source,
    content_tags: [entry.source],
    content_title: entry.title || ""
  };
}